import React from "react";
import { useParams, Link } from "react-router-dom";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Check, Phone, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const SERVICE_LABELS = {
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  basement: "Basement",
  "whole-house": "Whole-House",
  exterior: "Exterior",
  other: "Other",
};

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        className="w-full flex items-center justify-between py-5 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-body font-semibold text-foreground pr-4">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-accent flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
      </button>
      {open && <p className="font-body text-muted-foreground pb-5 text-sm leading-relaxed">{answer}</p>}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();

  const { data: service, isLoading } = useQuery({
    queryKey: ["service", slug],
    queryFn: async () => {
      const results = await base44.entities.Services.filter({ slug, is_active: true });
      return results[0] || null;
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects-by-service", slug],
    queryFn: () => base44.entities.PortfolioProjects.filter({ service_type: slug }, "-completion_date", 6),
    initialData: [],
  });

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-muted border-t-accent rounded-full animate-spin" />
    </div>
  );

  if (!service) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="font-body text-muted-foreground">Service not found.</p>
    </div>
  );

  const faqItems = service.faq_items || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${service.title} in Baltimore, MD`,
        "provider": { "@type": "LocalBusiness", "name": "Top-Notch Contractors" },
        "areaServed": "Baltimore, MD",
      })}} />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end bg-primary overflow-hidden">
        {service.image_url && (
          <>
            <img loading="lazy" decoding="async" src={service.image_url} alt={service.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent" />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-3">Baltimore, MD</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white max-w-2xl leading-tight">
            {service.title} in Baltimore, MD | Top Notch Contractors
          </h1>
        </div>
      </section>

      {/* Content + Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main */}
          <div className="lg:col-span-2">
            <p className="font-body text-muted-foreground text-sm uppercase tracking-widest font-semibold mb-3">Overview</p>
            <p className="font-body text-foreground text-lg leading-relaxed mb-10">
              {service.description}
            </p>

            {service.features && service.features.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-foreground mb-5">What's Included</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-secondary/60 rounded-lg border border-border">
                      <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="font-body text-sm text-foreground">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {projects.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-foreground mb-6">Recent {service.title} Projects</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {projects.map((project) => {
                    const img = project.after_image || (project.gallery_images && project.gallery_images[0]);
                    return (
                      <Link key={project.id} to="/portfolio" className="group block bg-card rounded-xl overflow-hidden border border-border hover:shadow-md transition-all">
                        {img && <img loading="lazy" decoding="async" src={img} alt={project.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />}
                        <div className="p-4">
                          <h3 className="font-heading font-bold text-foreground text-base group-hover:text-accent transition-colors">{project.title}</h3>
                          {project.location && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground font-body mt-1">
                              <MapPin className="w-3 h-3" /> {project.location}
                            </p>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* FAQs */}
            {faqItems.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Frequently Asked Questions</h2>
                <div className="mt-4">
                  {faqItems.map((faq, i) => (
                    <FAQItem key={i} question={faq.question} answer={faq.answer} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-5">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold text-foreground mb-4">Get a Free Estimate</h3>
                <p className="font-body text-sm text-muted-foreground mb-5">
                  No obligation. We walk your space and give you a fixed-price quote.
                </p>
                <Link to="/contact">
                  <Button className="w-full bg-accent text-white hover:bg-accent/90 font-body font-semibold mb-3">
                    Request Free Estimate
                  </Button>
                </Link>
                <a href="tel:4105989933">
                  <Button variant="outline" className="w-full border-primary/30 text-primary hover:bg-primary hover:text-white font-body transition-colors">
                    <Phone className="w-4 h-4 mr-2" /> (410) 598-9933
                  </Button>
                </a>
              </div>
              <div className="bg-secondary/60 border border-border rounded-xl p-5">
                <div className="space-y-2 font-body text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground">Trust &amp; Licensing</p>
                  <p>MHIC# 05-147079</p>
                  <p>PA HIC# PA177445</p>
                  <p>Licensed &amp; Insured</p>
                  <p>Since 2010 · 500+ Projects</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}